// deps
import React, { ReactElement, useMemo, useCallback, memo } from "react";

// internal-deps
import { VALID_PAYMENT_GROUP_TYPES } from "@swiggy-private/payment-types";

// interfaces
import {
    CardPaymentMethodData,
    CodPaymentMethodData,
    CredPaymentMethod,
    NetBankingPaymentGroupActions,
    NetBankingPaymentMethodData,
    PayLaterPaymentGroupActions,
    PayLaterPaymentMethodData,
    PaymentMethod,
    PaymentMethodMeta,
    FlipkartSuperCoinsPaymentMethodData,
    SwiggyInstrumentsPaymentGroupActions,
    SwiggyInstrumentsPaymentMethodData,
    UpiPaymentGroupActions,
    WalletPaymentGroupActions,
    WalletPaymentMethodData,
    InAppUpiPaymentMethod,
    InAppUpiPaymentGroupActions,
} from "@payment-ui/interfaces";
import { PreferredRendererProps } from "@payment-ui/components/payment-group/preferred/interfaces";

// components
import Card from "@payment-ui/components/payment-method/card";
import Cod from "@payment-ui/components/payment-method/cod";
import NetBanking from "@payment-ui/components/payment-method/netbanking";
import Upi from "@payment-ui/components/payment-method/upi";
import PayLater from "@payment-ui/components/payment-method/pay-later";
import { SwiggyInstrumentsPaymentMethod as SwiggyInstrumentsMethod } from "@payment-ui/components/payment-method/swiggy-instruments";
import { FlipkartSuperCoinPaymentMethod as FlipkartSuperCoinsMethod } from "@payment-ui/components/payment-method/flipkart-super-coins";
import Wallet from "@payment-ui/components/payment-method/wallet";
import Cred from "@payment-ui/components/payment-method/cred";
import InAppUpi from "@payment-ui/components/payment-method/inapp-upi";

function PreferredMethodRenderer(props: PreferredRendererProps): ReactElement | null {
    const {
        paymentAmount,
        user,
        methodData,
        getPaymentActions,
        getPaymentMethodProps,
        credEligibleData,
        juspayMeta,
        paymentGroupBanner,
    } = props;
    const groupName = useMemo(() => methodData.groupName.toUpperCase(), [methodData.groupName]);
    const methodProps = useMemo(() => getPaymentMethodProps(methodData), [getPaymentMethodProps, methodData]);
    const paymentMethodActions = useMemo(() => getPaymentActions(groupName), [getPaymentActions, groupName]);
    const juspayTokenizationMeta = useMemo(() => juspayMeta?.tokenizationMeta, [juspayMeta]);

    const renderUpiMethod = useCallback(
        (paymentMethod: PaymentMethod<PaymentMethodMeta>) => {
            const upiMethodProps = {
                ...methodProps,
                paymentAmount,
                methodData: paymentMethod,
                paymentActions: paymentMethodActions as UpiPaymentGroupActions,
            };

            return <Upi {...upiMethodProps} />;
        },
        [methodProps, paymentAmount, paymentMethodActions],
    );

    switch (groupName) {
        case VALID_PAYMENT_GROUP_TYPES.CARD:
        case VALID_PAYMENT_GROUP_TYPES.FOOD_CARD: {
            return (
                <Card
                    {...methodProps}
                    paymentActions={paymentMethodActions}
                    methodData={methodData as CardPaymentMethodData}
                    paymentAmount={paymentAmount}
                    juspayTokenizationMeta={juspayTokenizationMeta}
                    isPreferredPaymentCard={groupName === VALID_PAYMENT_GROUP_TYPES.CARD}
                />
            );
        }
        case VALID_PAYMENT_GROUP_TYPES.WALLET:
        case VALID_PAYMENT_GROUP_TYPES.WALLET_WEB:
            return (
                <Wallet
                    {...methodProps}
                    user={user}
                    paymentActions={paymentMethodActions as WalletPaymentGroupActions}
                    methodData={methodData as WalletPaymentMethodData}
                    paymentAmount={paymentAmount}
                />
            );

        case VALID_PAYMENT_GROUP_TYPES.FLIPKART_SUPER_COIN:
            return (
                <FlipkartSuperCoinsMethod
                    {...methodProps}
                    paymentAmount={paymentAmount}
                    paymentActions={paymentMethodActions}
                    methodData={methodData as FlipkartSuperCoinsPaymentMethodData}
                />
            );

        case VALID_PAYMENT_GROUP_TYPES.COD: {
            return (
                <Cod
                    {...methodProps}
                    paymentAmount={paymentAmount}
                    paymentActions={paymentMethodActions}
                    methodData={methodData as CodPaymentMethodData}
                />
            );
        }

        case VALID_PAYMENT_GROUP_TYPES.NETBANKING:
            return (
                <NetBanking
                    {...methodProps}
                    paymentActions={paymentMethodActions as NetBankingPaymentGroupActions}
                    methodData={methodData as NetBankingPaymentMethodData}
                    paymentAmount={paymentAmount}
                    isPreferredMethod
                />
            );

        case VALID_PAYMENT_GROUP_TYPES.UPI:
            return renderUpiMethod(methodData);

        case VALID_PAYMENT_GROUP_TYPES.PAY_LATER:
            return user != null ? (
                <PayLater
                    {...methodProps}
                    user={user}
                    paymentActions={paymentMethodActions as PayLaterPaymentGroupActions}
                    methodData={methodData as PayLaterPaymentMethodData}
                    paymentAmount={paymentAmount}
                />
            ) : null;

        case VALID_PAYMENT_GROUP_TYPES.SWIGGY_INSTRUMENTS:
            return (
                <SwiggyInstrumentsMethod
                    {...methodProps}
                    paymentAmount={paymentAmount}
                    paymentActions={paymentMethodActions as SwiggyInstrumentsPaymentGroupActions}
                    methodData={methodData as SwiggyInstrumentsPaymentMethodData}
                    paymentGroupBanner={paymentGroupBanner}
                />
            );

        case VALID_PAYMENT_GROUP_TYPES.CRED:
            if (!credEligibleData) {
                return null;
            }
            return (
                <Cred
                    {...methodProps}
                    paymentActions={paymentMethodActions}
                    methodData={methodData as CredPaymentMethod}
                    bannerText={credEligibleData.bannerText}
                    isEligible={credEligibleData.isEligible}
                    isSdkAvailable={credEligibleData.isSdkAvailable}
                    isOfferLoading={credEligibleData.isOfferLoading}
                />
            );

        case VALID_PAYMENT_GROUP_TYPES.INAPP_UPI: {
            return (
                <InAppUpi
                    {...methodProps}
                    methodData={methodData as InAppUpiPaymentMethod}
                    paymentActions={paymentMethodActions as InAppUpiPaymentGroupActions}
                    paymentAmount={paymentAmount}
                />
            );
        }
        default:
            return null;
    }
}

export default memo(PreferredMethodRenderer);
